import { useEffect, useState } from "react";
import { Swords, MapPin, CalendarDays } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const fmtDate = (d) => {
  try {
    return new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return d;
  }
};

export default function MyMatches() {
  const { user } = useAuth();
  const [matches, setMatches] = useState([]);

  useEffect(() => {
    if (user) api.get("/fighter/matches").then((r) => setMatches(r.data)).catch(() => {});
  }, [user]);

  const upcoming = matches.filter((m) => m.status === "scheduled");
  const past = matches.filter((m) => m.status === "completed");

  const resultFor = (m) => {
    if (m.winner_user_id === "draw") return { label: "DRAW", cls: "text-yellow-500 border-yellow-800/60" };
    return m.winner_user_id === user?.id
      ? { label: "WIN", cls: "text-green-500 border-green-800/60" }
      : { label: "LOSS", cls: "text-red-500 border-red-800/60" };
  };

  return (
    <section className="border border-zinc-800 bg-[#111111] p-6 sm:p-8" data-testid="my-matches-section">
      <p className="font-mono2 text-[10px] tracking-[0.35em] text-zinc-500 uppercase mb-6 flex items-center gap-2">
        <Swords className="h-4 w-4 text-[#E10600]" /> My Matches
      </p>

      {matches.length === 0 ? (
        <p className="font-mono2 text-[10px] tracking-[0.3em] text-zinc-600 uppercase" data-testid="matches-empty">
          No bouts scheduled yet — the matchmaking committee will be in touch
        </p>
      ) : (
        <div className="space-y-6">
          {upcoming.length > 0 && (
            <div className="space-y-3" data-testid="upcoming-matches">
              {upcoming.map((m) => {
                const opp = m.fighter1_user_id === user?.id ? m.fighter2_name : m.fighter1_name;
                return (
                  <div key={m.id} className="border border-[#E10600]/40 bg-[#161010] p-5" data-testid={`upcoming-match-${m.id}`}>
                    <p className="font-mono2 text-[9px] tracking-[0.3em] text-[#E10600] uppercase mb-2">Upcoming Bout</p>
                    <p className="font-display text-3xl sm:text-4xl uppercase text-white">
                      You <span className="text-[#E10600]">vs</span> {opp}
                    </p>
                    <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 font-mono2 text-[10px] tracking-[0.2em] text-zinc-400 uppercase">
                      <span className="flex items-center gap-2"><CalendarDays className="h-3.5 w-3.5 text-[#E10600]" /> {fmtDate(m.date)}</span>
                      <span className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5 text-[#E10600]" /> {m.venue}</span>
                      <span>{m.rounds} rounds</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {past.length > 0 && (
            <div className="space-y-3" data-testid="past-matches">
              {past.map((m) => {
                const opp = m.fighter1_user_id === user?.id ? m.fighter2_name : m.fighter1_name;
                const r = resultFor(m);
                return (
                  <div key={m.id} className="border border-zinc-800 bg-[#0d0d0d] p-5 flex items-center justify-between gap-4 flex-wrap" data-testid={`past-match-${m.id}`}>
                    <div>
                      <p className="font-cond font-bold text-xl uppercase text-white">You vs {opp}</p>
                      <p className="font-mono2 text-[10px] tracking-[0.2em] text-zinc-500 uppercase mt-1">
                        {fmtDate(m.date)} · {m.venue}{m.method ? ` · ${m.method}` : ""}{m.score ? ` · ${m.score}` : ""}
                      </p>
                    </div>
                    <span className={`font-display text-2xl border px-4 py-1.5 ${r.cls}`} data-testid={`match-result-${m.id}`}>{r.label}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
